(() => {
  const doc = document;
  const body = doc.body;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const lockBody = () => body.classList.add('is-locked');
  const unlockBody = () => {
    if (!doc.querySelector('.is-open')) body.classList.remove('is-locked');
  };

  const intro = doc.querySelector('[data-site-intro]');
  if (intro) {
    let hasSeenIntro = false;
    try { hasSeenIntro = sessionStorage.getItem('pawn-intro-seen') === 'true'; } catch (error) { hasSeenIntro = false; }
    const finishIntro = () => {
      intro.classList.add('is-complete');
      try { sessionStorage.setItem('pawn-intro-seen', 'true'); } catch (error) {}
      window.setTimeout(() => intro.remove(), 900);
    };
    if (hasSeenIntro || reducedMotion) finishIntro();
    else window.setTimeout(finishIntro, 1650);
  }

  const header = doc.querySelector('[data-site-header]');
  if (header) {
    const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 44);
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });
  }

  const mobileMenu = doc.querySelector('[data-mobile-menu]');
  const menuOpenButton = doc.querySelector('[data-menu-open]');
  const closeMenu = () => {
    if (!mobileMenu) return;
    mobileMenu.classList.remove('is-open');
    mobileMenu.setAttribute('aria-hidden', 'true');
    menuOpenButton?.setAttribute('aria-expanded', 'false');
    window.setTimeout(unlockBody, 420);
  };
  const openMenu = () => {
    if (!mobileMenu) return;
    mobileMenu.classList.add('is-open');
    mobileMenu.setAttribute('aria-hidden', 'false');
    menuOpenButton?.setAttribute('aria-expanded', 'true');
    lockBody();
    mobileMenu.querySelector('[data-menu-close]')?.focus();
  };
  menuOpenButton?.addEventListener('click', openMenu);
  doc.querySelectorAll('[data-menu-close]').forEach((button) => button.addEventListener('click', closeMenu));

  const revealItems = doc.querySelectorAll('.reveal');
  if (reducedMotion || !('IntersectionObserver' in window)) {
    revealItems.forEach((item) => item.classList.add('is-visible'));
  } else {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -5% 0px' });
    revealItems.forEach((item) => observer.observe(item));
  }

  if (!reducedMotion) {
    const heroStage = doc.querySelector('[data-hero-stage]');
    if (heroStage) {
      let ticking = false;
      const moveHero = () => {
        const rect = heroStage.closest('.pawn-hero')?.getBoundingClientRect();
        if (rect && rect.bottom > 0) {
          const shift = Math.max(-24, Math.min(34, window.scrollY * 0.045));
          heroStage.style.transform = `translate3d(0, ${shift}px, 0)`;
        }
        ticking = false;
      };
      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(moveHero);
          ticking = true;
        }
      }, { passive: true });
    }
  }

  doc.querySelectorAll('[data-card-gallery]').forEach((gallery) => {
    const track = gallery.querySelector('[data-card-gallery-track]');
    const dots = Array.from(gallery.querySelectorAll('.product-card__dot'));
    if (!track || dots.length < 2) return;
    let galleryFrame;
    const updateGallery = () => {
      const slideWidth = track.clientWidth || 1;
      const activeIndex = Math.max(0, Math.min(dots.length - 1, Math.round(track.scrollLeft / slideWidth)));
      dots.forEach((dot, index) => dot.classList.toggle('is-active', index === activeIndex));
      galleryFrame = undefined;
    };
    track.addEventListener('scroll', () => {
      if (!galleryFrame) galleryFrame = window.requestAnimationFrame(updateGallery);
    }, { passive: true });
    updateGallery();
  });

  const toast = doc.querySelector('[data-toast]');
  let toastTimer;
  const showToast = (message) => {
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  const cartDrawer = doc.querySelector('[data-cart-drawer]');
  const cartContent = doc.querySelector('[data-cart-drawer-content]');
  const cartCountNodes = doc.querySelectorAll('[data-cart-count]');
  const openCart = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    lockBody();
    cartDrawer.querySelector('[data-cart-close]')?.focus();
  };
  const closeCart = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    window.setTimeout(unlockBody, 420);
  };
  doc.querySelectorAll('[data-cart-open]').forEach((button) => button.addEventListener('click', async () => {
    await refreshCart();
    openCart();
  }));
  doc.querySelectorAll('[data-cart-close]').forEach((button) => button.addEventListener('click', closeCart));

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const formatMoney = (cents, currency = 'EGP') => {
    try {
      return new Intl.NumberFormat(doc.documentElement.lang || 'en', {
        style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  };

  const renderCart = (cart) => {
    cartCountNodes.forEach((node) => { node.textContent = cart.item_count; });
    if (!cartContent) return;
    if (!cart.item_count) {
      cartContent.innerHTML = `
        <div class="cart-drawer__empty">
          <p>Your next move starts here.</p>
          <a class="button button--dark" href="/collections/all">Shop the drop</a>
        </div>`;
      return;
    }
    const items = cart.items.map((item) => `
      <article class="drawer-item">
        ${item.image ? `<img src="${escapeHtml(item.image)}&width=240" width="120" height="120" loading="lazy" alt="${escapeHtml(item.product_title)}">` : ''}
        <div>
          <a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          <p>${escapeHtml(item.variant_title || '')}${item.quantity > 1 ? ` × ${item.quantity}` : ''}</p>
          <p>${formatMoney(item.final_line_price, cart.currency)}</p>
        </div>
      </article>`).join('');
    cartContent.innerHTML = `
      <div class="cart-drawer__items">${items}</div>
      <div class="cart-drawer__footer">
        <div class="cart-drawer__total"><span>Subtotal</span><strong>${formatMoney(cart.total_price, cart.currency)}</strong></div>
        <a class="button button--outline" href="/cart">View bag</a>
        <a class="button button--dark" href="/checkout">Checkout</a>
      </div>`;
  };

  async function refreshCart() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Could not load cart');
      const cart = await response.json();
      renderCart(cart);
      return cart;
    } catch (error) {
      showToast('Unable to load your bag');
      return null;
    }
  }

  doc.querySelectorAll('form[data-ajax-cart]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submit = form.querySelector('[type="submit"]');
      const originalText = submit?.textContent;
      if (submit) { submit.disabled = true; submit.textContent = 'ADDING…'; }
      try {
        const response = await fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/json' }
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || 'Could not add item');
        await refreshCart();
        openCart();
        showToast('Added to your bag');
      } catch (error) {
        showToast(error.message || 'Choose an available option');
      } finally {
        if (submit) { submit.disabled = false; submit.textContent = originalText; }
      }
    });
  });

  doc.querySelectorAll('[data-variant-select]').forEach((select) => {
    const form = select.closest('form');
    const price = form?.closest('[data-product-panel]')?.querySelector('[data-product-price]');
    const submit = form?.querySelector('[type="submit"]');
    const updateVariant = () => {
      const option = select.options[select.selectedIndex];
      if (price && option.dataset.price) {
        const onSale = option.dataset.onSale === 'true';
        const compareAtPrice = option.dataset.compareAtPrice || '';
        price.innerHTML = `${onSale && compareAtPrice ? `<s>${compareAtPrice}</s>` : ''}<strong class="product-price__current">${option.dataset.price}</strong>${onSale ? '<span class="product-price__sale-badge">SALE</span>' : ''}`;
      }
      if (submit) {
        const available = option.dataset.available === 'true';
        submit.disabled = !available;
        submit.textContent = available ? 'ADD TO BAG' : 'SOLD OUT';
      }
    };
    select.addEventListener('change', updateVariant);
    updateVariant();
  });

  doc.querySelectorAll('[data-quantity]').forEach((wrapper) => {
    const input = wrapper.querySelector('input');
    wrapper.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
      input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
    });
    wrapper.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
      input.value = Number(input.value || 1) + 1;
    });
  });

  doc.querySelectorAll('[data-film]').forEach((frame) => {
    const video = frame.querySelector('video');
    const button = frame.querySelector('[data-film-toggle]');
    const poster = frame.querySelector('[data-film-poster]');
    if (!video || !button) return;
    button.addEventListener('click', () => {
      if (video.paused) video.play(); else video.pause();
    });
    video.addEventListener('play', () => {
      button.setAttribute('aria-label', 'Pause film');
      button.classList.add('is-playing');
      poster?.classList.add('is-hidden');
    });
    video.addEventListener('pause', () => {
      button.setAttribute('aria-label', 'Play film');
      button.classList.remove('is-playing');
    });
  });

  doc.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeMenu();
    closeCart();
  });
})();
